import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Responsive utility functions for grid classes and spacing

export interface ResponsiveGridConfig {
  mobile: number
  tablet: number
  desktop: number
}

/**
 * Generates responsive grid classes based on column configuration
 * @param config - Grid configuration for different screen sizes
 * @returns Tailwind CSS grid classes string
 */
export function getResponsiveGridClasses(config: ResponsiveGridConfig): string {
  const { mobile, tablet, desktop } = config
  
  return cn(
    `grid-cols-${mobile}`,
    `md:grid-cols-${tablet}`,
    `lg:grid-cols-${desktop}`
  )
}

/**
 * Generates responsive spacing classes
 * @param spacing - Spacing configuration for different screen sizes
 * @returns Tailwind CSS spacing classes string
 */
export function getResponsiveSpacing(spacing: {
  mobile?: string
  tablet?: string
  desktop?: string
  property?: 'p' | 'm' | 'px' | 'py' | 'mx' | 'my' | 'pt' | 'pb' | 'pl' | 'pr' | 'mt' | 'mb' | 'ml' | 'mr'
}): string {
  const { mobile = '4', tablet = '6', desktop = '8', property = 'p' } = spacing
  
  return cn(
    `${property}-${mobile}`,
    `md:${property}-${tablet}`,
    `lg:${property}-${desktop}`
  )
}

/**
 * Generates responsive container classes with appropriate padding
 * @returns Tailwind CSS container classes string
 */
export function getResponsiveContainer(): string {
  return cn(
    'container',
    'mx-auto',
    'px-4', // 16px on mobile
    'md:px-6', // 24px on tablet
    'lg:px-8' // 32px on desktop
  )
}

/**
 * Generates responsive text size classes
 * @param sizes - Text size configuration for different screen sizes
 * @returns Tailwind CSS text size classes string
 */
export function getResponsiveTextSize(sizes: {
  mobile?: string
  tablet?: string
  desktop?: string
}): string {
  const { mobile = 'sm', tablet = 'base', desktop = 'lg' } = sizes
  
  return cn(
    `text-${mobile}`,
    `md:text-${tablet}`,
    `lg:text-${desktop}`
  )
}

/**
 * Generates responsive flex direction classes
 * @param directions - Flex direction configuration for different screen sizes
 * @returns Tailwind CSS flex direction classes string
 */
export function getResponsiveFlexDirection(directions: {
  mobile?: 'row' | 'col' | 'row-reverse' | 'col-reverse'
  tablet?: 'row' | 'col' | 'row-reverse' | 'col-reverse'
  desktop?: 'row' | 'col' | 'row-reverse' | 'col-reverse'
}): string {
  const { mobile = 'col', tablet = 'row', desktop = 'row' } = directions
  
  return cn(
    `flex-${mobile}`,
    `md:flex-${tablet}`,
    `lg:flex-${desktop}`
  )
}

/**
 * Generates responsive gap classes
 * @param gaps - Gap configuration for different screen sizes
 * @returns Tailwind CSS gap classes string
 */
export function getResponsiveGap(gaps: {
  mobile?: string
  tablet?: string
  desktop?: string
}): string {
  const { mobile = '4', tablet = '6', desktop = '8' } = gaps
  
  return cn(
    `gap-${mobile}`,
    `md:gap-${tablet}`,
    `lg:gap-${desktop}`
  )
}

/**
 * Utility to check if current screen size matches a breakpoint
 * @param breakpoint - The breakpoint to check against
 * @param width - Current screen width (defaults to window.innerWidth)
 * @returns Boolean indicating if screen matches breakpoint
 */
export function matchesBreakpoint(
  breakpoint: 'mobile' | 'tablet' | 'desktop',
  width?: number
): boolean {
  const currentWidth = width ?? (typeof window !== 'undefined' ? window.innerWidth : 1024)
  
  switch (breakpoint) {
    case 'mobile':
      return currentWidth < 768
    case 'tablet':
      return currentWidth >= 768 && currentWidth < 1024
    case 'desktop':
      return currentWidth >= 1024
    default:
      return false
  }
}

/**
 * Generates responsive width classes
 * @param widths - Width configuration for different screen sizes
 * @returns Tailwind CSS width classes string
 */
export function getResponsiveWidth(widths: {
  mobile?: string
  tablet?: string
  desktop?: string
}): string {
  const { mobile = 'full', tablet = 'full', desktop = 'full' } = widths
  
  return cn(
    `w-${mobile}`,
    `md:w-${tablet}`,
    `lg:w-${desktop}`
  )
}

/**
 * Generates responsive height classes
 * @param heights - Height configuration for different screen sizes
 * @returns Tailwind CSS height classes string
 */
export function getResponsiveHeight(heights: {
  mobile?: string
  tablet?: string
  desktop?: string
}): string {
  const { mobile = 'auto', tablet = 'auto', desktop = 'auto' } = heights
  
  return cn(
    `h-${mobile}`,
    `md:h-${tablet}`,
    `lg:h-${desktop}`
  )
}
